import { Divider } from "@mui/material";
import { format } from "date-fns";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import React from "react";
import AreaAvatar from "../../components/Map/AreaAvatar";
import Helper from "../../Helper";
import { Dropoff } from "../../models/Dropoff";
import { MapObject } from "../../models/MapObject";
import { CommandListDelivery, CommandProjectInfo } from "../CommandTypes";

dayjs.extend(timezone);

interface CommandListViewDateSectionProps {
    label?: string;
    date: Date;
    deliveries: CommandListDelivery[];
    project: CommandProjectInfo;
}

const CommandListViewDateSection: React.FC<CommandListViewDateSectionProps> = ({
    label,
    date,
    deliveries,
    project,
}) => {
    const dropoffAvatar = (delivery: CommandListDelivery) => {
        if (!delivery?.dropoff) {
            return null;
        }

        const avatar = (
            <AreaAvatar
                mappable={Dropoff.fromApiObject(delivery.dropoff, project.timezone)}
                mapObject={
                    delivery.dropoff_map_object
                        ? MapObject.fromApiObject(delivery.dropoff_map_object, project.timezone)
                        : null
                }
            />
        );

        return (
            <div className="flex items-center text-nowrap gap-1">
                {avatar}
                <span className="truncate">{delivery.dropoff.name}</span>
            </div>
        );
    };

    const gateAvatar = (delivery: CommandListDelivery) => {
        if (!delivery?.gate || !delivery?.gate_map_object) {
            return null;
        }

        const avatar = <AreaAvatar mapObject={MapObject.fromApiObject(delivery.gate_map_object, project.timezone)} />;

        return (
            <div className="flex items-center text-nowrap gap-1">
                {avatar}
                <span className="truncate">{delivery.gate.name}</span>
            </div>
        );
    };

    const equipmentAvatar = (delivery: CommandListDelivery) => {
        if (!delivery?.equipment || !delivery?.equipment_map_object) {
            return null;
        }

        const avatar = (
            <AreaAvatar mapObject={MapObject.fromApiObject(delivery.equipment_map_object, project.timezone)} />
        );

        return (
            <div className="flex items-center text-nowrap gap-1">
                {avatar}
                <span className="truncate">{delivery.equipment.equipment_name}</span>
            </div>
        );
    };

    const vehicleLabel = (delivery: CommandListDelivery): React.ReactNode => {
        if (delivery.vehicle_quantity > 1) {
            return <span className="mx-1">({delivery.vehicle_quantity})</span>;
        }

        return null;
    };

    const renderDelivery = (delivery: CommandListDelivery, renderFuture: boolean = false) => {
        const startAt = dayjs(delivery?.start_at)?.tz(project.timezone);
        return (
            <div key={delivery.id} className="flex flex-row items-center justify-between text-plot-white">
                <div
                    className={Helper.classNames(
                        "text-nowrap font-bold text-center w-1/6",
                        startAt?.format("LTS").length == 10 && "pl-2"
                    )}
                >
                    {renderFuture ? startAt?.format("MM/dd h:mm A") : startAt?.format("h:mm A")}
                </div>
                <div className="flex flex-row items-center w-1/4 text-left">
                    <img
                        src={delivery.receiving_team?.avatar_url}
                        alt={delivery.receiving_team?.team_name}
                        className="w-5 h-5 rounded-full"
                    />
                    <span className="ml-2 truncate">{delivery.receiving_team?.team_name}</span>
                </div>
                <div className="font-bold text-left w-1/3">
                    <span className="truncate">{delivery.name}</span>
                    {vehicleLabel(delivery)}
                </div>
                <div className="w-1/6">{gateAvatar(delivery)}</div>
                <div className="w-1/5">{dropoffAvatar(delivery)}</div>
                <div className="w-1/5">{equipmentAvatar(delivery)}</div>
            </div>
        );
    };

    return (
        <>
            <Divider>
                <span className=" font-plot-open-sans text-lg text-plot-orange ">
                    {label && `${label}, `}
                    {`${format(date, "MMM do ")}`}
                </span>
            </Divider>
            <div className="flex flex-col gap-2">
                {deliveries.length > 0 ? (
                    deliveries.map((delivery) => renderDelivery(delivery))
                ) : (
                    <div className="w-full text-center text-lg italic text-plot-light-grey ">No Deliveries {label}</div>
                )}
            </div>
        </>
    );
};

export default CommandListViewDateSection;
